import { useCallback, useEffect, useRef, useState } from "react";
import { message, Spin, Tag } from "antd";
import DataTable from "../../components/common/DataTable";
import DashboardFilterBar from "./components/DashboardFilterBar";
import KPICard from "../../components/common/KPICard";
import {
  PERFORMANCE_KPI_DEFS,
  ALL_VALUE,
  defaultPerformanceFilters,
} from "./constants";
import { getDashboardPerformance } from "../../services/dashboard.service";

const PAGE_SIZE = 25;

const EMPTY_KPIS = PERFORMANCE_KPI_DEFS.reduce(
  (acc, kpi) => ({ ...acc, [kpi.key]: null }),
  {}
);

/**
 * Strip the "All" sentinel. Year/Month and the From/To range are mutually
 * exclusive server-side, so only whichever the user actually set is sent.
 */
function toQueryParams(filters, page) {
  const params = { page, per_page: PAGE_SIZE };

  if (filters.fromDate || filters.toDate) {
    if (filters.fromDate) params.fromDate = filters.fromDate;
    if (filters.toDate) params.toDate = filters.toDate;
  } else if (filters.year && filters.year !== ALL_VALUE) {
    params.year = filters.year;
    if (filters.month && filters.month !== ALL_VALUE) params.month = filters.month;
  }

  if (filters.project && filters.project !== ALL_VALUE) params.project = filters.project;
  if (filters.fleetType && filters.fleetType !== ALL_VALUE) params.fleetType = filters.fleetType;
  if (filters.city && filters.city !== ALL_VALUE) params.city = filters.city;
  if (filters.rider && filters.rider !== ALL_VALUE) params.rider = filters.rider;
  if (filters.zones?.length) params.zones = filters.zones.join(",");

  return params;
}

const COLUMNS = [
  {
    title: "Partner ID",
    dataIndex: "riderId",
    key: "riderId",
    className: "dash-mono",
  },
  {
    title: "Partner Name",
    dataIndex: "fullName",
    key: "fullName",
    render: (name, row) => (
      <>
        {name || "—"}
        {row.isDeleted && (
          <Tag color="red" className="dash-performer-tag">
            DELETED
          </Tag>
        )}
        {!row.isDeleted && row.status === "inactive" && (
          <Tag className="dash-performer-tag">INACTIVE</Tag>
        )}
      </>
    ),
  },
  {
    title: "Zone",
    dataIndex: "zoneName",
    key: "zoneName",
    render: (zone) => zone || "—",
  },
  {
    // All-time by definition — unaffected by the date filters above.
    title: "Last Worked Date",
    dataIndex: "lastWorkedDate",
    key: "lastWorkedDate",
    className: "dash-mono",
    render: (date) => date || "—",
  },
  {
    title: "Orders",
    dataIndex: "orders",
    key: "orders",
  },
  {
    title: "Fuel Card",
    dataIndex: "fuelCard",
    key: "fuelCard",
    render: () => "—",
  },
  {
    title: "Fuel Consumed",
    dataIndex: "fuelConsumed",
    key: "fuelConsumed",
    render: () => "—",
  },
];

export default function PerformanceTab() {
  const [filters, setFilters] = useState(defaultPerformanceFilters);
  const [kpis, setKpis] = useState(EMPTY_KPIS);
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    total_count: 0,
  });
  const [loading, setLoading] = useState(false);

  // Overlapping requests: discard a response whose filters are already stale.
  const request = useRef(0);

  const load = useCallback(async (activeFilters, page) => {
    const requestId = (request.current += 1);
    setLoading(true);
    try {
      const data = await getDashboardPerformance(
        toQueryParams(activeFilters, page)
      );
      if (requestId !== request.current) return;
      setKpis({ ...EMPTY_KPIS, ...(data?.kpis || {}) });
      setRows(data?.records || []);
      setPagination({
        page: data?.pagination?.page || 1,
        total_count: data?.pagination?.total_count || 0,
      });
    } catch {
      if (requestId !== request.current) return;
      setKpis(EMPTY_KPIS);
      setRows([]);
      setPagination({ page: 1, total_count: 0 });
      message.error("Failed to load performance data");
    } finally {
      if (requestId === request.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(filters, 1);
  }, [filters, load]);

  const handleRefresh = () => {
    setFilters(defaultPerformanceFilters());
  };

  return (
    <div className="dash-tab">
      <DashboardFilterBar
        values={filters}
        onChange={setFilters}
        onRefresh={handleRefresh}
        showYearMonth
        yearMonthFirst
        showAllOption
        multiZone
        allowAllPeriod
        showDateRange
        showPartners
        singlePartner
      />

      <div className="dash-body">
        <Spin spinning={loading}>
          <div className="dash-kpi-grid cols-4">
            {PERFORMANCE_KPI_DEFS.map((kpi) => (
              <KPICard
                key={kpi.key}
                label={kpi.label}
                value={kpi.placeholder ? null : kpis[kpi.key]}
                unit={kpi.unit}
              />
            ))}
          </div>

          <div className="dash-table-card dash-perf-table">
            <DataTable
              columns={COLUMNS}
              dataSource={rows}
              rowKey="_id"
              pagination={{
                current: pagination.page,
                pageSize: PAGE_SIZE,
                total: pagination.total_count,
                showSizeChanger: false,
                onChange: (page) => load(filters, page),
                showTotal: (total, range) =>
                  total
                    ? `${range[0]} — ${range[1]} of ${total} records`
                    : "0 records",
              }}
            />
          </div>
        </Spin>
      </div>
    </div>
  );
}
